(() => {
  const selector = '[data-native-predictive]';
  const delay = 220;

  class NativePredictiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('[name="q"]');
      this.page = form.hasAttribute('data-native-predictive-page');
      this.wrapper = this.page ? form : form.closest('.header__search-bar-wrapper');
      this.results = form.querySelector(this.page ? '[data-native-page-results]' : '.search-bar__results');
      this.inner = this.page ? null : form.querySelector('.search-bar__inner');
      this.target = form.querySelector('[data-native-predictive-results]');
      this.menu = form.querySelector('.search-bar__menu-wrapper');
      this.typeSelect = form.querySelector('#search-product-type');
      this.timer = null;
      this.controller = null;
      this.requestId = 0;
      this.dismissed = false;
      this.viewportListening = false;
      this.layoutFrame = null;
      this.layoutObserver = null;
      this.updateViewport = this.updateViewport.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.handleDocumentClick = event => {
        if (!this.wrapper.contains(event.target)) this.close();
      };
      this.handleBrowseUpdate = event => {
        if (event.detail.root.contains(this.form)) this.destroy();
      };
      this.handleSectionUnload = event => {
        if (event.target.contains(this.form)) this.destroy();
      };
      if (!this.input || !this.results || !this.target || !this.wrapper) return;
      this.bind();
    }

    bind() {
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-expanded', 'false');
      this.results.setAttribute('role', 'region');
      this.input.addEventListener('focus', () => this.onFocus());
      this.input.addEventListener('input', () => this.onInput());
      this.form.addEventListener('keydown', event => this.onKeydown(event));
      this.form.addEventListener('click', event => this.onClick(event));
      this.form.addEventListener('submit', event => this.onSubmit(event));
      this.form.addEventListener('focusout', () => {
        setTimeout(() => {
          if (this.form.isConnected && !this.form.contains(document.activeElement)) this.close();
        }, 0);
      });
      this.typeSelect?.addEventListener('change', () => this.onTypeChange());
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      if (this.page) document.addEventListener('native:browse:before-update', this.handleBrowseUpdate);
    }

    onFocus() {
      this.dismissed = false;
      if (!this.page && (this.input.value.trim() || this.menu)) {
        this.wrapper.classList.add('is-fixed');
        document.body.classList.add('no-mobile-scroll');
      }
      if (this.input.value.trim()) {
        if (this.target.childElementCount > 0) this.open();
        this.schedule();
      } else if (this.menu) {
        this.menu.setAttribute('aria-hidden', 'false');
        this.form.classList.add('is-expanded');
      }
    }

    onInput() {
      this.dismissed = false;
      this.input.classList.toggle('is-filled', Boolean(this.input.value));
      this.abort();
      clearTimeout(this.timer);
      this.hideResults();
      this.target.innerHTML = '';
      if (!this.input.value.trim()) {
        if (this.menu) {
          this.menu.setAttribute('aria-hidden', 'false');
          this.form.classList.add('is-expanded');
        } else if (!this.page) {
          this.wrapper.classList.remove('is-fixed');
          document.body.classList.remove('no-mobile-scroll');
        }
        return;
      }
      if (!this.page) {
        this.wrapper.classList.add('is-fixed');
        document.body.classList.add('no-mobile-scroll');
      }
      if (this.menu) this.menu.setAttribute('aria-hidden', 'true');
      this.schedule();
    }

    onTypeChange() {
      const label = this.form.querySelector('.search-bar__filter-active');
      if (label) label.textContent = this.typeSelect.selectedOptions[0]?.textContent || '';
      this.onInput();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.results.getAttribute('aria-hidden') === 'false' || this.form.classList.contains('is-expanded') || this.wrapper.classList.contains('is-fixed')) {
          event.preventDefault();
          this.input.focus();
          this.close();
        }
        return;
      }
      if (event.isComposing || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
      const options = [...this.target.querySelectorAll('[data-native-predictive-option], .search-bar__result-item, .search-bar__view-all')].filter(option => option instanceof HTMLElement && option.offsetParent !== null);
      if (this.results.getAttribute('aria-hidden') !== 'false' || options.length === 0) return;
      const current = options.indexOf(document.activeElement);
      if (document.activeElement !== this.input && current < 0) return;
      event.preventDefault();
      const next = document.activeElement === this.input
        ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
        : current + (event.key === 'ArrowDown' ? 1 : -1);
      if (next < 0 || next >= options.length) this.input.focus();
      else options[next].focus();
    }

    onClick(event) {
      if (event.target.closest('[data-native-predictive-close]')) {
        this.input.focus();
        this.close();
        return;
      }
      if (event.target.closest('[data-action="unfix-search"]')) {
        this.close();
        return;
      }
      if (event.target.closest('[data-action="clear-input"]')) {
        this.input.value = '';
        this.onInput();
        this.input.focus();
        return;
      }
      if (event.target.closest('[data-native-predictive-option]')) this.close();
    }

    onSubmit(event) {
      this.close();
      if (!this.typeSelect?.value) return;
      event.preventDefault();
      const url = new URL(this.form.action, window.location.origin);
      const query = this.input.value.trim();
      url.searchParams.set('q', `product_type:${this.typeSelect.value}${query ? ` AND ${query}` : ''}`);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      const unavailable = this.form.dataset.unavailableProducts;
      if (['show', 'hide', 'last'].includes(unavailable)) url.searchParams.set('options[unavailable_products]', unavailable);
      window.location.assign(url.href);
    }

    schedule() {
      clearTimeout(this.timer);
      const query = this.input.value.trim();
      if (query) this.timer = setTimeout(() => this.fetchResults(query), delay);
    }

    async fetchResults(query) {
      this.abort();
      const controller = new AbortController();
      this.controller = controller;
      const requestId = ++this.requestId;
      const type = this.typeSelect?.value || '';
      const scoped = Boolean(type);
      const url = scoped
        ? new URL(this.form.action, window.location.origin)
        : new URL(this.form.dataset.suggestUrl || `${window.Shopify?.routes?.root || '/'}search/suggest`, window.location.origin);
      url.searchParams.set('q', scoped ? `product_type:${type} AND ${query}` : query);
      if (scoped) {
        url.searchParams.set('section_id', 'native-predictive-search');
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        const unavailable = this.form.dataset.unavailableProducts;
        if (['show', 'hide', 'last'].includes(unavailable)) url.searchParams.set('options[unavailable_products]', unavailable);
      } else {
        url.searchParams.set('section_id', 'native-predictive-search');
        url.searchParams.set('resources[type]', 'product,query,collection');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[limit]', '5');
        const unavailable = this.form.dataset.unavailableProducts;
        if (['show', 'hide', 'last'].includes(unavailable)) {
          url.searchParams.set('resources[options][unavailable_products]', unavailable);
        }
      }
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`Search returned ${response.status}`);
        const html = await response.text();
        if (controller.signal.aborted || requestId !== this.requestId || this.input.value.trim() !== query || (this.typeSelect?.value || '') !== type || this.dismissed) return;
        const documentFromResponse = new DOMParser().parseFromString(html, 'text/html');
        const content = documentFromResponse.querySelector('[data-native-predictive-results]');
        if (!content) throw new Error('Search section missing');
        content.querySelectorAll('[id]').forEach(element => {
          const original = element.id;
          const unique = `${this.results.id}-${original}`;
          content.querySelectorAll('[aria-labelledby], [aria-controls], [for]').forEach(reference => {
            ['aria-labelledby', 'aria-controls', 'for'].forEach(attribute => {
              if (reference.hasAttribute(attribute)) reference.setAttribute(attribute, reference.getAttribute(attribute).split(' ').map(value => value === original ? unique : value).join(' '));
            });
          });
          element.id = unique;
        });
        this.target.innerHTML = content.innerHTML;
        this.target.classList.toggle('native-predictive--scoped', scoped);
        this.open();
        this.results.scrollTop = 0;
      } catch (error) {
        if (error.name !== 'AbortError' && requestId === this.requestId) this.hideResults();
      }
    }

    open() {
      if (this.menu) this.menu.setAttribute('aria-hidden', 'true');
      if (!this.page) {
        this.wrapper.classList.add('is-fixed');
        document.body.classList.add('no-mobile-scroll');
      }
      this.results.setAttribute('aria-hidden', 'false');
      this.input.setAttribute('aria-expanded', 'true');
      this.form.classList.add('is-expanded');
      document.body.classList.add('native-predictive-open');
      this.startViewportTracking();
      this.updateViewport();
      this.remeasureAfterLayout();
    }

    hideResults() {
      this.results.setAttribute('aria-hidden', 'true');
      this.input.setAttribute('aria-expanded', 'false');
      if (this.menu?.getAttribute('aria-hidden') !== 'false') this.form.classList.remove('is-expanded');
      if (!document.querySelector(`${selector} .search-bar__results[aria-hidden="false"], ${selector} [data-native-page-results][aria-hidden="false"]`)) {
        document.body.classList.remove('native-predictive-open');
      }
    }

    close() {
      this.dismissed = true;
      clearTimeout(this.timer);
      this.abort();
      this.hideResults();
      if (this.menu) this.menu.setAttribute('aria-hidden', 'true');
      this.form.classList.remove('is-expanded');
      if (!this.page) {
        this.wrapper.classList.remove('is-fixed');
        document.body.classList.remove('no-mobile-scroll');
      }
      this.stopViewportTracking();
      if (this.layoutFrame !== null) {
        window.cancelAnimationFrame?.(this.layoutFrame);
        this.layoutFrame = null;
      }
    }

    remeasureAfterLayout() {
      if (!window.requestAnimationFrame) return;
      if (this.layoutFrame !== null) window.cancelAnimationFrame?.(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => {
        this.updateViewport();
        this.layoutFrame = window.requestAnimationFrame(() => {
          this.updateViewport();
          this.layoutFrame = null;
        });
      });
    }

    updateViewport() {
      if (this.results.getAttribute('aria-hidden') !== 'false') return;
      const viewport = window.visualViewport;
      const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const resultsTop = this.results.getBoundingClientRect().top;
      const available = Math.max(96, Math.floor(viewportBottom - resultsTop - 8));
      this.form.style.setProperty('--native-predictive-available-height', `${available}px`);
      if (this.inner && window.innerWidth > 640) {
        this.form.style.removeProperty('--native-predictive-horizontal-adjust');
        const bounds = this.inner.getBoundingClientRect();
        const viewportLeft = viewport?.offsetLeft || 0;
        const viewportRight = viewportLeft + (viewport?.width || window.innerWidth);
        const leftLimit = viewportLeft + 16;
        const rightLimit = viewportRight - 16;
        let shift = 0;
        if (bounds.right > rightLimit) shift = rightLimit - bounds.right;
        if (bounds.left + shift < leftLimit) shift = leftLimit - bounds.left;
        this.form.style.setProperty('--native-predictive-horizontal-adjust', `${shift}px`);
      }
    }

    onViewportChange() {
      this.updateViewport();
      this.remeasureAfterLayout();
    }

    startViewportTracking() {
      if (this.viewportListening) return;
      this.viewportListening = true;
      window.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onViewportChange);
      if (window.ResizeObserver) {
        this.layoutObserver = new window.ResizeObserver(() => this.updateViewport());
        const header = this.wrapper.closest('.header');
        const announcement = document.querySelector('#shopify-section-announcement-bar');
        if (header) this.layoutObserver.observe(header);
        if (announcement) this.layoutObserver.observe(announcement);
      }
    }

    stopViewportTracking() {
      if (!this.viewportListening) return;
      this.viewportListening = false;
      window.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
      this.layoutObserver?.disconnect();
      this.layoutObserver = null;
    }

    destroy() {
      this.close();
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      document.removeEventListener('native:browse:before-update', this.handleBrowseUpdate);
    }

    abort() {
      this.controller?.abort();
      this.controller = null;
      this.requestId++;
    }
  }

  function init(root = document) {
    root.querySelectorAll(selector).forEach(form => {
      if (form.dataset.nativePredictiveReady) return;
      form.dataset.nativePredictiveReady = 'true';
      new NativePredictiveSearch(form);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('native:browse:updated', event => init(event.detail.root));
})();
